<template>
  <div v-if="currentView === 'record'">
    <RecordPage :socket="socket" :sendMessage="sendMessage" :handleMessage="processMessage" @send="goToDetails" />
  </div>
  <div v-if="currentView === 'translate'">
    <TranslatePage :socket="socket" :sendMessage="sendMessage" :handleMessage="processMessage" @send="goToDetails" />
  </div>
  <div v-else-if="currentView === 'details'">
    <TopNav :patientName="patientName" />
    <div class="px-4 encounterCont">
      <!-- RAG toggle -->
      <div class="px-4 mt-3 d-flex justify-content-end align-items-center">
        <div class="form-check form-switch">
          <input
            class="form-check-input"
            type="checkbox"
            id="ragSwitch"
            v-model="ragMode"
          />
          <label class="form-check-label" for="ragSwitch">Kortex</label>
        </div>
      </div>
      <div ref="chatContainer" class="chat-container">
        <div v-for="message in data.responseData" :key="message.id" :data-id="message.id" :class="['message-row', message.isuser ? 'is-user' : 'is-sofia']" >
        <div class="meta me-2">
              <span v-if="message.isuser" class="status ms-2">
                <i v-if="message.sendStatus === 'sending'" class="fa fa-spinner fa-spin" title="Sending..."></i>

                <!-- stays visible after success -->
                <i v-else-if="message.sendStatus === 'sent'" class="fa fa-check text-success" title="Sent"></i>

                <button
                  v-else-if="message.sendStatus === 'failed'"
                  class="btn btn-link p-0 text-danger"
                  @click="retrySend(message)"
                  title="Retry"
                >
                  <i class="fa fa-rotate-right"></i>
                </button>

                <small v-if="message.sendStatus === 'failed'" class="text-danger opacity-75 ms-1">
                  Failed to send
                </small>
              </span>
            </div>
          <div class="bubble" :class="message.isuser ? 'bubble-user' : 'bubble-sofia'" >
            <div class="content" v-html="formatMessage(message.message)"></div>
              <span class="timestamp">{{ new Date(message.created_at).toLocaleString() }}</span>

            <!-- actions for Sofia only -->
            <div v-if="!message.isuser" class="message-actions d-flex gap-2 mt-2">
              <button @click="toggleSpeak(message)" class="btn-color">
                <i v-if="message.isLoading" class="fa-solid fa-spinner fa-spin"></i>
                <i v-else :class="message.isSpeaking ? 'fa-solid fa-pause' : 'fa-solid fa-volume-up'"></i>
              </button>
              <button @click="copyToClipboard(message.message)" class="btn-color">
                <i class="fas fa-copy"></i>
              </button>
              <button @click="sendPdf(message.message)" class="btn-color">
                <i class="fas fa-file-pdf"></i>
              </button>
              <button @click="shareMessage(message.message)" class="btn-color">
                <i class="fas fa-share-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="loadingMessage" class="text-center mt-3" style="display: flex;align-items: center;justify-content: center;">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
      </div>
      <div class="button-container ">
        
        <div v-if="addNote || ragMode" class="pt-4 w-100 d-flex px-3">
          <textarea v-model="note" placeholder="Write your note here..." class="form-control me-2" >
          </textarea>
          <span v-if="isSending" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span v-else class="d-flex">
            <button class="btn text-white mic-button me-2" style="background-color:#518DB1;" @click="toggleRecording">
              <i :class="micIcon"></i>
            </button>
            <button @click="handleButtonClick(note, 'Note')" class="btn text-white"  style="background-color:#518DB1;">
              <i class="fa-solid fa-circle-arrow-right"></i>
            </button>
          </span>
        </div>
        <div class="d-flex flex-wrap gap-2 justify-content-center py-4 col-12 col-md-10">
          <template v-if="!ragMode">
            <button v-for="btn in transformedButtons" :key="btn.original" @click="handleButtonClick(btn.label, btn.label)" class="btn text-white" style="background-color:#518DB1;">
              {{ btn.label }}
            </button>
          </template>
          <button  @click="openWarning"  class="btn text-white" style="background-color:#518DB1;" v-if="!loadingMessage && !ragMode">
            Add Note
          </button>
        </div>
          <!-- Bootstrap Modal -->
          <div v-if="showWarning" class="modal fade show d-block" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header bg-warning text-white">
                  <!-- <h5 class="modal-title">Warning</h5> -->
                  <button type="button" class="btn-close" @click="showWarning = false"></button>
                </div>
                <div class="text-center">
                  <i class="fa-solid fa-triangle-exclamation  text-warning" style="font-size:100px;"></i>
                  <p><strong>Warning!</strong></p>
                  <p>If you add a note now, previous data will be changed.</p>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-danger" @click="confirmChange">
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUpdated, onUnmounted,onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import  JWTinterceptor  from "@/services/axios";
import TopNav from "../layouts/topNav.vue";
import { useSpeak } from "../utils/useSpeech";
import RecordPage from "./RecordPage.vue";
import TranslatePage from "./TranslatePage.vue";
import { useRecorder } from "../utils/useRecord2";
import { getPatientByIdService } from "@/services/api/patientService";
import { getTopicChatService } from "@/services/api/encounterService"
import { sendPdfEmailService } from "@/services/api/commonService";
import { copyToClipboard ,shareMessage, formatMessage, removeChart} from "@/utils/commonFunctions";

// eslint-disable-next-line no-unused-vars
const { speakText, stopSpeaking, isLoading } = useSpeak("es");
const language = ref("en");
const { isRecording, transcription, startRecording, stopRecording } = useRecorder(language);
const store = useStore();
const navigationData = computed(() => store.getters['navigation/getNavigationData']);
const topicId = computed(() => store.state.topicId);
const data = ref({ responseData: [] });
const loading = ref(true);
const text = ref("");
const dialogId = "2";
const token = localStorage.getItem("token");
const patientName = ref('');
const patientId = computed(() => store.state.patientId);
const wsUrl = computed(() =>`${process.env.VUE_APP_SOFA_WS}${topicId.value}/${dialogId}/${patientId.value}`);
// const sentMessages = ref([]);
const receivedResponses = ref([]);
const toast = useToast();
const chatContainer = ref(null);
const buttons = ref([]);
const transformedButtons = ref([]);
const currentView = ref("details");
const socket = computed(() => store.state.websocket.socket);
const loadingMessage = ref(false);
const addNote = ref(false);
const note = ref("" || transcription);
const micIcon = ref("fa-solid fa-microphone");
const showWarning = ref(false);
const isNoteChanged = ref(false);
const isSending = ref(false);
const ragMode = ref(false);

const sendMessage = (message) => {
  store.dispatch("websocket/sendMessage", message);
  loadingMessage.value = true;
};
const openWarning = () => {
    if (!addNote.value) {
    showWarning.value = true; 
  } else {
    addNote.value = false; 
  }
};
const confirmChange = () => {
  addNote.value = true; 
  showWarning.value = false;
};

// Function to process incoming WebSocket messages
const processMessage = (receivedData) => {
  if (receivedData.source === "sofia") {
    receivedResponses.value.push(receivedData.response);
    // console.log("Received Sofia response:", receivedData.response);
  } else if (receivedData && receivedData.message) {
    const receivedMessage = {
      id: new Date().getTime(),
      message: receivedData.responseData.message,
      created_at: new Date().toISOString(),
      isuser: false,
    };
    if (data.value && data.value.responseData) {
      data.value.responseData.push(receivedMessage);
    } else {
      data.value = {
        responseData: [receivedMessage],
      };
    }
    // console.log("Message received via WebSocket:", receivedMessage);
    receivedResponses.value.push(receivedMessage.message);
  } else {
    // console.log("Received unrecognized WebSocket message:", receivedData);
  }
  loadingMessage.value = false;
};
const toggleRecording = () => {
  if (isRecording.value) {
    // console.log(isRecording.value);
    stopRecording();
    micIcon.value = "fa-solid fa-microphone";
  } else {
    startRecording();
    micIcon.value = "fa-solid fa-pause";
  }
};
const scrollToBottom = () => {
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};
const connectWS = () => {
  store.dispatch('websocket/connect', {
    wsUrl: wsUrl.value,
    token,
    handleMessage: processMessage, 
  })
}
onMounted(() => {
  if (navigationData.value.view === "record") {
    currentView.value = "record";
  }else if(navigationData.value.view === "translate"){
    currentView.value = "translate";
  }
  if (typeof navigationData.value.ragMode !== 'undefined') {
    ragMode.value = !!navigationData.value.ragMode;
  }
  connectWS();
  getBtn();
  getPatientById();

  if (topicId.value && token) {
    let chatId = topicId.value
    getTopicChat(chatId);
  } else {
    toast.error("Chat ID or Access Token is missing");
    console.error("Chat ID or Access Token is missing");
  }
  scrollToBottom();
});
onUpdated(scrollToBottom);
watch([topicId, patientName], async () => {
  try {
    await store.dispatch('websocket/closeConnection')
  } finally {
    connectWS()
    getTopicChat(topicId.value)
    getPatientById()
  }
})
watch(
  navigationData,
  (val) => {
    if (val && typeof val.ragMode !== 'undefined') {
      ragMode.value = !!val.ragMode;
    }
  },
  { immediate: true }
);
watch(
  () => navigationData.value.view,
  (newView) => {
    currentView.value = newView
  }
);
watch(data, (newVal, oldVal) => {
  if (newVal.responseData.length !== oldVal.responseData.length) {
    scrollToBottom();
  }
});
watch(note, (newValue) => {
  if (newValue !== '') {
    isNoteChanged.value = true; 
  } else {
    isNoteChanged.value = false;
  }
});

const goToDetails = () => {
  currentView.value = "details";
  const data = {  view: 'details' };
      store.dispatch('navigation/saveNavigationData', data);
  getTopicChat(topicId.value);
};

const getTopicChat = async (topicId) => {
  loadingMessage.value = true;
  const response = await getTopicChatService(topicId);
  data.value = response;
  console.log("Response received in details:", response.responseData);
  loadingMessage.value = false;
  receivedResponses.value.push(
    JSON.stringify(response.responseData.message)
  );
  loading.value = false;
};

const sendMsg = async (msg, cat) => {
  if (msg.trim().length > 0) {
    // const now = new Date().toISOString();
    const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const messagePayload = {
      id: tempId,   
      message: msg.trim(),
      is_draft: 0,
      category_id: cat.trim(),
      chat_id: topicId.value || 0,
      message_id: "12",
      created_at: new Date().toISOString(),
      isuser: true,
      sendStatus: 'sending',      
      error: null,  
    };

    data.value.responseData.push(messagePayload);

    // ✅ actually try to send
    sendMessage(messagePayload);
 
    if (loadingMessage.value) {
      // mark as sent
      markMessageStatus(tempId, "sent");
    } else {
      // mark as failed (will show retry button)
      markMessageStatus(tempId, "failed", "WebSocket not open");
    }
  }
};

const markMessageStatus = (id, status, error = null) => {
  const m = data.value.responseData.find((x) => x.id === id);
  if (m) {
    m.sendStatus = status;
    m.error = error;
  }
};
const retrySend = (message) => {
  // set back to sending
  message.sendStatus = 'sending';
  message.error = null;

  try {
    const ok = sendMessage(message);
    if (!ok) throw new Error('WebSocket not open');
    markMessageStatus(message.id, 'sent');
  } catch (err) {
    markMessageStatus(message.id, 'failed', err?.message || 'Failed to send');
    toast.error('Still unable to send.');
  }
};

const sendPdf = async (msg) => {
  const result = await sendPdfEmailService(msg);
  toast.success("Mail Sent successfully");

  return result;
};

const toggleSpeak = async (message) => {
  if (message.isSpeaking) {
    stopSpeaking();
    message.isSpeaking = false;
  } else {
    data.value.responseData.forEach((message) => {
      message.isSpeaking = false;
      message.isLoading = false;
    });
    message.isLoading = true;
    try {
      await speakText(removeChart(message.message));
      message.isSpeaking = true;
    } catch (error) {
      console.error("Error speaking text:", error);
    } finally {
      message.isLoading = false;
    }
  }
};

const getBtn = async () => {
  try {
    const result = await JWTinterceptor.get(
      `messages_map`,
      {
        headers: {
          "x-access-tokens": token,
        },
      }
    );
    buttons.value = result.data;
    transformedButtons.value = buttons.value.map((btn) => {
      const capitalized = btn
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      return { original: btn, label: capitalized };
    });
  } catch (error) {
    console.error("Error fetching buttons:", error);
  }
};
const handleButtonClick = (buttonMsg, buttonText) => {
  text.value = buttonText;
  text.value = buttonMsg;

  if (buttonText === 'Note' && ragMode.value) {
    sendMsg(buttonMsg, 'rag');
    note.value = "";
    return;
  }
  // Find the message that matches the button text
  const existingMessage = data.value.responseData.find(
    (msg) => msg.message === buttonText
  );

  if (existingMessage) {
    // console.log('Found existing message with id:', existingMessage.id);
    const messageElement = document.querySelector(
      `[data-id="${existingMessage.id}"]`
    );

    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      console.log('Scrolled to message with id:', existingMessage.id);
    } else {
      console.error('Message element with id', existingMessage.id, 'not found.');
    }
  } else {
  sendMsg(buttonMsg, buttonText); // If the message doesn't exist, send the new message
  note.value = "";
  }
};

const handleBeforeUnload = (event) => {
  if (isNoteChanged.value) {
    const message = 'You have unsaved data. Are you sure you want to leave?';
    event.returnValue = message; 
    return message; 
  }
};

window.addEventListener('beforeunload', handleBeforeUnload);

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
  store.dispatch('websocket/closeConnection')

});


onUnmounted(() => {
  store.dispatch("websocket/closeConnection");

  data.value.responseData.forEach((msg) => {
    if (msg.isSpeaking) {
      stopSpeaking();
      msg.isSpeaking = false;
    }
  });
});
const getPatientById = async () => {
  const result = await getPatientByIdService(patientId.value);
  // DOB.value = result.date_of_birth;
  patientName.value = result.name
  // encounterNumber.value = result.number_of_encounters
};
</script>

<style scoped>
.card {
  background-color: #f8f9fa;
}
.timestamp {
  font-size: 0.85rem;
  color: white;
}
.message-body {
  font-size: 1rem;
}
.button-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: end;
  position: sticky;
  bottom: 0;
}
.message-color-user {
  background-color: #518DB1;
}
.message-color-sofia .timestamp,
.message-color-sofia {
  background-color: #D9F7C3;
  color: #383939 !important;
}
.btn-color {
  color: #518DB1;
  background-color: transparent;
  border: none;
  font-size: 1.7rem;
  margin-right: 20px;
}

.dropdown-menu-up {
  top: auto;
  bottom: 100%;
  left: 0;
  transform: translateY(-10px);
  position: absolute;
  z-index: 1000;
}
ul {
  list-style-type: none;
  padding: 5px;
}
ul button {
  margin-bottom: 5px;
}
.dropup-menu {
  top: auto;
  bottom: 100%;
  left: 0;
  position: absolute;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.chat-container {
  max-height: 95%;
  overflow-y: auto;
  padding: 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

}
.message-row {
  display: flex;
  width: 100%;
}
.message-row.is-user {
  justify-content: flex-end;
}
.message-row.is-sofia {
  justify-content: flex-start;
}
.bubble-user {
  background-color: #518DB1;
  color: #fff;
  padding: 15px;
  border-radius: 10px;
  max-width: 90%;
}
.bubble-sofia {
  background-color: #D9F7C3;
  color: #383939;
  padding: 15px;
  border-radius: 10px;
  max-width: 90%;
}

/* inner content spacing */
.content p {
  margin: 0;
}

/* meta line under content */
.meta {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.35rem;
  font-size: 0.82rem;
  opacity: 0.9;
}
.timestamp {
  color: currentColor; /* inherit bubble’s color */
}

/* status icons sizing */
.status .fa,
.status button {
  line-height: 1;
}

/* keep your existing buttons look */
.btn-color {
  color: #518DB1;
  background-color: transparent;
  border: none;
  font-size: 1.3rem;
  margin-right: 12px;
}
.encounterCont{
  margin-top: 3.5rem;
  display: flex;
  flex-direction: column;
  height: 90vh;
  overflow: hidden;
  background-color: white;
}
@media (min-width: 2045px) {
  .encounterCont{
  height: 94vh;
  }
  /* .chat-container {
  max-height: 52rem;
  } */
}
@media (max-width: 1708px) {
  .encounterCont{
  height: 92vh;
  }
  /* .chat-container {
  max-height: 42rem;
  } */
}
.message-body .fa-rotate-right { opacity: 0.85; }
.message-body .fa-check { opacity: 0.85; }

</style>
